/**
 * Müşteri Yönetimi Servis Katmanı
 *
 * Personel paneli için müşteri CRUD işlemlerini yönetir.
 * Supabase client üzerinden RLS korumalı sorgular çalıştırır.
 *
 * @module customerService
 */

import { supabase } from '../lib/supabase';

const customerService = {
  // ===========================================================
  // Müşteri Listesi
  // ===========================================================

  /**
   * Müşteri listesini getirir. Filtreleme ve sayfalama desteği vardır.
   * @param {Object} filters - Filtreleme seçenekleri
   * @param {string} [filters.status] - new | contacted | interested | not_interested | closed
   * @param {string} [filters.lead_source] - Lead kaynağı
   * @param {string} [filters.staff_id] - Staff UUID
   * @param {string} [filters.search] - İsim veya email arama
   * @param {number} [filters.limit=50] - Sayfa başına kayıt sayısı
   * @param {number} [filters.offset=0] - Kaydırma miktarı
   * @param {string} [filters.sortBy='created_at'] - Sıralama alanı
   * @param {boolean} [filters.ascending=false] - Artan sıralama
   * @returns {{ data: array|null, count: number, error: object|null }}
   */
  async getCustomers(filters = {}) {
    try {
      const {
        status,
        lead_source,
        staff_id,
        search,
        limit = 50,
        offset = 0,
        sortBy = 'created_at',
        ascending = false,
      } = filters;

      let query = supabase
        .from('customers')
        .select('*', { count: 'exact' });

      // Filtreler
      if (status) {
        query = query.eq('status', status);
      }
      if (lead_source) {
        query = query.eq('lead_source', lead_source);
      }
      if (staff_id) {
        query = query.eq('staff_id', staff_id);
      }
      if (search) {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
      }

      // Bugün follow-up'u olanları filtrelemek için
      if (filters.todayFollowUp) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayISO = today.toISOString();
        const tomorrowISO = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString();
        query = query.gte('next_follow_up', todayISO).lt('next_follow_up', tomorrowISO);
      }

      // Sıralama
      query = query.order(sortBy, { ascending });

      // Sayfalama
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      return { data, count, error };
    } catch (error) {
      console.error('getCustomers error:', error);
      return { data: null, count: 0, error };
    }
  },

  // ===========================================================
  // Tek Müşteri
  // ===========================================================

  /**
   * Tek bir müşterinin detayını aktiviteleriyle birlikte getirir.
   * @param {string} customerId - Müşteri UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async getCustomerById(customerId) {
    try {
      const [customerResult, activitiesResult] = await Promise.all([
        supabase
          .from('customers')
          .select('*')
          .eq('id', customerId)
          .single(),

        supabase
          .from('customer_activities')
          .select('*')
          .eq('customer_id', customerId)
          .order('activity_date', { ascending: false }),
      ]);

      if (customerResult.error) {
        return { data: null, error: customerResult.error };
      }

      return {
        data: {
          ...customerResult.data,
          activities: activitiesResult.data || [],
        },
        error: null,
      };
    } catch (error) {
      console.error('getCustomerById error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Müşteri Oluştur
  // ===========================================================

  /**
   * Yeni bir müşteri oluşturur.
   * @param {Object} customerData - Müşteri verileri
   * @param {string} customerData.first_name - Ad
   * @param {string} customerData.last_name - Soyad
   * @param {string} [customerData.email] - E-posta
   * @param {string} [customerData.phone] - Telefon
   * @param {string} [customerData.notes] - Notlar
   * @param {string} [customerData.status] - Durum
   * @param {Object} [customerData.budget_range] - Bütçe aralığı
   * @param {Array} [customerData.preferred_locations] - Tercih edilen lokasyonlar
   * @param {Array} [customerData.property_types] - Tercih edilen ilan tipleri
   * @param {string} [customerData.lead_source] - Lead kaynağı
   * @param {string} [customerData.next_follow_up] - Sonraki follow-up tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async createCustomer(customerData) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert({
          first_name: customerData.first_name,
          last_name: customerData.last_name,
          email: customerData.email || null,
          phone: customerData.phone || null,
          notes: customerData.notes || null,
          status: customerData.status || 'new',
          budget_range: customerData.budget_range || {},
          preferred_locations: customerData.preferred_locations || [],
          property_types: customerData.property_types || [],
          lead_source: customerData.lead_source || null,
          next_follow_up: customerData.next_follow_up || null,
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('createCustomer error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Müşteri Güncelle
  // ===========================================================

  /**
   * Mevcut bir müşteriyi günceller.
   * @param {string} customerId - Müşteri UUID'si
   * @param {Object} customerData - Güncellenecek veriler
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateCustomer(customerId, customerData) {
    try {
      // Güncellenebilir alanlar
      const allowedFields = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'notes',
        'status',
        'budget_range',
        'preferred_locations',
        'property_types',
        'lead_source',
        'next_follow_up',
      ];

      const updateData = {};
      allowedFields.forEach(field => {
        if (customerData[field] !== undefined) {
          updateData[field] = customerData[field];
        }
      });

      // last_contacted_at güncellenecekse
      if (customerData.last_contacted_at) {
        updateData.last_contacted_at = customerData.last_contacted_at;
      }

      const { data, error } = await supabase
        .from('customers')
        .update(updateData)
        .eq('id', customerId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('updateCustomer error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Müşteri Sil
  // ===========================================================

  /**
   * Bir müşteriyi siler.
   * @param {string} customerId - Müşteri UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async deleteCustomer(customerId) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      return { data, error };
    } catch (error) {
      console.error('deleteCustomer error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Müşteri Aktiviteleri
  // ===========================================================

  /**
   * Bir müşterinin aktivitelerini getirir.
   * @param {string} customerId - Müşteri UUID'si
   * @returns {{ data: array|null, error: object|null }}
   */
  async getCustomerActivities(customerId) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .select('*')
        .eq('customer_id', customerId)
        .order('activity_date', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('getCustomerActivities error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir müşteriye aktivite ekler.
   * @param {string} customerId - Müşteri UUID'si
   * @param {Object} activityData - Aktivite verileri
   * @param {string} activityData.activity_type - call | email | meeting | note | property_showing | follow_up
   * @param {string} activityData.title - Aktivite başlığı
   * @param {string} [activityData.description] - Aktivite açıklaması
   * @param {string} [activityData.activity_date] - Aktivite tarihi
   * @returns {{ data: object|null, error: object|null }}
   */
  async addActivity(customerId, activityData) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .insert({
          customer_id: customerId,
          activity_type: activityData.activity_type,
          title: activityData.title,
          description: activityData.description || null,
          activity_date: activityData.activity_date || new Date().toISOString(),
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('addActivity error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir aktiviteyi günceller.
   * @param {string} activityId - Aktivite UUID'si
   * @param {Object} activityData - Güncellenecek veriler
   * @returns {{ data: object|null, error: object|null }}
   */
  async updateActivity(activityId, activityData) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .update(activityData)
        .eq('id', activityId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('updateActivity error:', error);
      return { data: null, error };
    }
  },

  /**
   * Bir aktiviteyi siler.
   * @param {string} activityId - Aktivite UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async deleteActivity(activityId) {
    try {
      const { data, error } = await supabase
        .from('customer_activities')
        .delete()
        .eq('id', activityId);

      return { data, error };
    } catch (error) {
      console.error('deleteActivity error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Toplu İşlemler
  // ===========================================================

  /**
   * Birden fazla müşterinin durumunu günceller.
   * @param {Array<string>} customerIds - Müşteri UUID'leri
   * @param {string} status - Yeni durum
   * @returns {{ data: object|null, error: object|null }}
   */
  async bulkUpdateStatus(customerIds, status) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .update({ status })
        .in('id', customerIds)
        .select();

      return { data, error };
    } catch (error) {
      console.error('bulkUpdateStatus error:', error);
      return { data: null, error };
    }
  },

  /**
   * Müşterilere toplu follow-up tarihi atar.
   * @param {Array<string>} customerIds - Müşteri UUID'leri
   * @param {string} followUpDate - Follow-up tarihi (ISO string)
   * @returns {{ data: object|null, error: object|null }}
   */
  async bulkAssignFollowUp(customerIds, followUpDate) {
    try {
      const { data, error } = await supabase
        .from('customers')
        .update({ next_follow_up: followUpDate })
        .in('id', customerIds)
        .select();

      return { data, error };
    } catch (error) {
      console.error('bulkAssignFollowUp error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // Arama ve Filtreleme
  // ===========================================================

  /**
   * Müşterilerde gelişmiş arama yapar.
   * @param {Object} searchParams - Arama parametreleri
   * @param {string} [searchParams.name] - İsim
   * @param {string} [searchParams.email] - E-posta
   * @param {string} [searchParams.phone] - Telefon
   * @param {string} [searchParams.status] - Durum
   * @param {string} [searchParams.lead_source] - Lead kaynağı
   * @param {number} [searchParams.minBudget] - Minimum bütçe
   * @param {number} [searchParams.maxBudget] - Maksimum bütçe
   * @param {Array<string>} [searchParams.locations] - Lokasyonlar
   * @returns {{ data: array|null, error: object|null }}
   */
  async searchCustomers(searchParams) {
    try {
      let query = supabase.from('customers').select('*');

      // İsim arama
      if (searchParams.name) {
        query = query.or(`first_name.ilike.%${searchParams.name}%,last_name.ilike.%${searchParams.name}%`);
      }

      // E-posta arama
      if (searchParams.email) {
        query = query.ilike('email', `%${searchParams.email}%`);
      }

      // Telefon arama
      if (searchParams.phone) {
        query = query.ilike('phone', `%${searchParams.phone}%`);
      }

      // Durum filtre
      if (searchParams.status) {
        query = query.eq('status', searchParams.status);
      }

      // Lead source filtre
      if (searchParams.lead_source) {
        query = query.eq('lead_source', searchParams.lead_source);
      }

      // Bütçe aralığı filtre (JSONB)
      if (searchParams.minBudget !== undefined || searchParams.maxBudget !== undefined) {
        const budgetConditions = [];
        if (searchParams.minBudget !== undefined) {
          budgetConditions.push(`(budget_range->>'max')::numeric >= ${searchParams.minBudget}`);
        }
        if (searchParams.maxBudget !== undefined) {
          budgetConditions.push(`(budget_range->>'min')::numeric <= ${searchParams.maxBudget}`);
        }
        if (budgetConditions.length > 0) {
          // Supabase client'ta JSONB filtreleme için raw SQL gerekir
          // Bu durumda RPC kullanmak daha iyidir
        }
      }

      // Lokasyon filtre (array overlap)
      if (searchParams.locations && searchParams.locations.length > 0) {
        query = query.overlaps('preferred_locations', searchParams.locations);
      }

      const { data, error } = await query;

      return { data, error };
    } catch (error) {
      console.error('searchCustomers error:', error);
      return { data: null, error };
    }
  },

  // ===========================================================
  // İstatistikler
  // ===========================================================

  /**
   * Müşteri istatistiklerini getirir.
   * @returns {{ data: object|null, error: object|null }}
   */
  async getCustomerStats() {
    try {
      const [totalResult, newResult, contactedResult, interestedResult, closedResult] = await Promise.all([
        supabase.from('customers').select('id', { count: 'exact', head: true }),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'new'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'contacted'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'interested'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('status', 'closed'),
      ]);

      return {
        data: {
          total: totalResult.count || 0,
          new: newResult.count || 0,
          contacted: contactedResult.count || 0,
          interested: interestedResult.count || 0,
          closed: closedResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getCustomerStats error:', error);
      return { data: null, error };
    }
  },

  /**
   * Belirli bir staff'ın müşteri istatistiklerini getirir.
   * @param {string} staffId - Staff UUID'si
   * @returns {{ data: object|null, error: object|null }}
   */
  async getStaffCustomerStats(staffId) {
    try {
      const [totalResult, newResult, interestedResult, closedResult] = await Promise.all([
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('staff_id', staffId),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('staff_id', staffId).eq('status', 'new'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('staff_id', staffId).eq('status', 'interested'),
        supabase.from('customers').select('id', { count: 'exact', head: true }).eq('staff_id', staffId).eq('status', 'closed'),
      ]);

      return {
        data: {
          total: totalResult.count || 0,
          new: newResult.count || 0,
          interested: interestedResult.count || 0,
          closed: closedResult.count || 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('getStaffCustomerStats error:', error);
      return { data: null, error };
    }
  },
};

export default customerService;
